import React, {memo, useState, useEffect, useCallback, useRef} from 'react';
import ServerSidePaginationAggrid from '../common/ServerSidePaginationAggrid.jsx';
import Turbolink from '../common/Turbolink.jsx';
import {Adalicon} from '../common/Icons.jsx';
import TooltipWithRef from '../common/Tooltip.jsx';
import MultilangValue from '../common/MultilangValue.jsx';

import {
  workflow_inbox_list_path,
  monitoring_report_project_reports_path,
  project_report_edit_report_path,
  project_report_review_path,
  project_report_decision_path,
  workflow_inbox_mark_as_read_path,
  project_lists_for_add_path,
  project_list_add_project_to_list_path,
} from '~/routes';

const PAGINATION_PAGE_SIZE = 10;
const PAGINATION_PAGE_SIZE_SELECTOR = [10, 25, 50, 100];

export const DATE_FILTER_COMPARATOR = {
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    const dateAsString = cellValue;

    if (_.isEmpty(dateAsString)) return 0;

    const cellDate = new Date(dateAsString).setHours(0,0,0,0);

    // Now that both parameters are Date objects, we can compare
    if (cellDate < filterLocalDateAtMidnight)
      return -1;
    else if (cellDate > filterLocalDateAtMidnight)
      return 1;

    return 0;
  }
};

// Key used on <a> links to define redirect destination after action
const REDIRECT_BACK_TO = 'workflow_inbox'

export const valueGetter = (params) => {
  params.data ||= {};
  return params.data[params.colDef.colId] || '-';
};

const ButtonsCellRenderer = memo((params) => {
  const {project_report_id, project_id, phase_id, current_status} = params.getValue();

  return (<>
    {current_status === 'pending_approval' ?
      <Turbolink
        href={project_report_decision_path({project_report_id, project_id, report_phase_id: phase_id, redirect_back_to: REDIRECT_BACK_TO})}
        className='btn btn-navy btn-sm'
      >
        <Adalicon type='gavel'/> {I18n.t('decision')}
      </Turbolink>
      : current_status === 'pending_review' &&
        <Turbolink
          href={project_report_review_path({project_report_id, project_id, report_phase_id: phase_id, redirect_back_to: REDIRECT_BACK_TO})}
          className='btn btn-navy btn-sm'
        >
          <Adalicon type='search'/> {I18n.t('review')}
        </Turbolink>
    }
  </>)
});

const ManageWorkflowInbox = ({permissions = {}}) => {
  const [externalFilter, setExternalFilter] = useState({});
  const projectListsRef = useRef([]);

  useEffect(() => {
    // Init the search bar as it render in ruby
    $('#workflowInboxGlobalFilterInput').on("keyup", function(e) {
      setExternalFilter((preVal) => ({...preVal, ['global_search']: e.target.value}))
    });

    // Load personal project lists only when the user has permission
    if (permissions.add_to_project_list) {
      fetch(project_lists_for_add_path())
        .then(res => res.json())
        .then(res => projectListsRef.current = res);
    }
  }, []);

  const addProjectToList = (selectedListId, projectId) => {
    jQuery.post(project_list_add_project_to_list_path(), {add_to_list: {project_list_id: selectedListId, project_id: projectId}})
      .done((_res) => {
        window.ToastNotification({message: I18n.t('project_added_to_list_successfully'), horizontal: 'center', vertical: 'top', autoHideDuration: 5});
      })
  }

  const onGridReady = useCallback((params) => {
    // Automatically resize all columns to fit available width
    params.api.sizeColumnsToFit();

    // Also re-fit columns when window is resized
    window.addEventListener('resize', () => {
      setTimeout(() => params.api.sizeColumnsToFit(), 100);
    });
  }, []);

  const getContextMenuItems = useCallback((params) => {
    const defaultItems = ['copy', 'copyWithHeaders', 'paste', 'separator', 'export'];
    const additionalItems = [];

    // Display personal project lists only when the user has permission and there are lists available
    if (permissions.add_to_project_list && projectListsRef.current.length) {
      additionalItems.push(
        'separator',
        {
          name: I18n.t('add_to_list'),
          icon: '<i class="fa fa-plus"></i>',
          subMenu: projectListsRef.current.map(list => ({name: list.name, action: () => addProjectToList(list.id, params.node.data.project_id)}))
        }
      );
    }
    return [...defaultItems, ...additionalItems];
  }, []);

  const handleMarkAsRead = useCallback((e, project_report_id) => {
    const el = e.currentTarget;
    el.innerHTML = `<i class="fa fa-spinner fa-spin"></i> ${I18n.t('read')}...`;

    jQuery.post(workflow_inbox_mark_as_read_path({project_report_id}), {}, () => {
      e.target.remove();
      jQuery(".tooltip.fade.top.in").remove();
    })
    .fail(() => {
      el.innerHTML = I18n.t('unread');
    });
  }, []);

  const COLUMN_DEFS = [
    {
      headerName: '',
      colId: 'is_read',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        optionMap: new Map([true, false].map(v => [v, I18n.t(v ? 'read' : 'unread')])),
        valueFormatter: ({value}) => value ? I18n.t('read') : I18n.t('unread'),
      },
      width: 85,
      minWidth: 85,
      cellStyle: { padding: 0 },
      suppressHeaderMenuButton: true,
      valueGetter: (params) => params.data[params.colDef.colId],
      cellRenderer: ({value, data: {project_report_id}}) => {
        if (value) return;
        return (
          <TooltipWithRef
            trigger='hover'
            title={I18n.t('mark_as_read')}
            style={{maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
          >
            <span
              className={`label label-success`}
              style={{cursor: 'pointer'}}
              onClick={(e) => handleMarkAsRead(e, project_report_id)}
            >
              {I18n.t('unread')}
            </span>
          </TooltipWithRef>
        )
      }
    },
    {
      field: I18n.t('workflow_name'),
      colId: 'workflow_name',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
      cellRenderer: ({data, colDef: {colId}}) => {
        const {monitoring_report_id} = data;
        return (
          <a href={monitoring_report_project_reports_path({id: monitoring_report_id})}>
            <MultilangValue value={data[colId]} valueLocale={data[`${colId}_locale`]}/>
          </a>
        )
      },
    },
    {
      field: I18n.t('type'),
      colId: 'report_type',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
      cellRenderer: ({value}) => {
        return I18n.t(`report_type_${value}`);
      },
    },
    {
      field: I18n.t('phase'),
      colId: 'phase_name',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
    },
    {
      field: I18n.t('status'),
      colId: 'current_status',
      editable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['pending_review', 'pending_approval'],
        optionMap: new Map(['pending_review', 'pending_approval'].map(v => [v, I18n.t(`project_report_current_status_${v}`)])),
        valueFormatter: ({value}) => I18n.t(`project_report_current_status_${value}`),
      },
      valueGetter: valueGetter,
      cellRenderer: ({value}) => I18n.t(`project_report_current_status_${value}`)
    },
    {
      field: I18n.t('assigned_at'),
      colId: 'assigned_at',
      editable: false,
      filter: 'agDateColumnFilter',
      valueGetter: valueGetter,
      valueFormatter: ({data: {assigned_at}}) => DT.dt(assigned_at, {format: null, datetime: true, timezone: false}),
      filterParams: {
        ...DATE_FILTER_COMPARATOR
      },
    },
    {
      field: I18n.t('project_report_identifier'),
      colId: 'project_report_identifier',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
      cellRenderer: (params) => {
        const {monitoring_report_id, project_report_id, project_id, project_report_identifier} = (params.data || {});
        return (
          <a href={project_report_edit_report_path({project_id, project_report_id, report_id: monitoring_report_id})}>{project_report_identifier}</a>
        )
      }
    },
    {
      field: I18n.t('project_identifier'),
      colId: 'project_identifier',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
    },
    {
      field: I18n.t('project_title_long'),
      colId: 'project_title',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
    },
    {
      headerName: '',
      colId: 'action_buttons',
      editable: false,
      filter: false,
      sortable: false,
      width: 120,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      cellRenderer: ButtonsCellRenderer,
      valueGetter: (params) => {
        params.data ||= {};
        return {...params.data};
      },
    },
    {
      field: '-',
      colId: 'global_search',
      editable: false,
      hide: true,
      lockVisible: true,
      filter: 'agTextColumnFilter',
      suppressColumnsToolPanel: true
    }
  ];

  return (
    <div>
      <ServerSidePaginationAggrid
        fetchPath={workflow_inbox_list_path({})}
        externalFilter={externalFilter}
        columnDefs={COLUMN_DEFS}
        onGridReady={onGridReady}
        defaultGridOptions={{
          pagination: true,
          paginationPageSizeSelector: PAGINATION_PAGE_SIZE_SELECTOR,
          paginationPageSize: PAGINATION_PAGE_SIZE,
          cacheBlockSize: PAGINATION_PAGE_SIZE,
          getContextMenuItems
        }}
        style={{paddingTop: '10px'}}
      />
    </div>
  );
};

export default ManageWorkflowInbox;
